import React, {  useState, ChangeEvent, useEffect, useCallback } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

import { distances, colors, border } from '../../../styles/constants';
import { AssetRequestObject } from '../../types';
import { Button } from '../../../components/Buttons';
import { Loading } from '../../../components/Loading';

interface Error {
    error: boolean;
    message: string;
}

interface ImageUploaderProps {
    createAsset: (asset: AssetRequestObject) => void;
    removeAsset: (discountId: string) => void;
    discountId: string;
    isLoyaltyCard?: boolean;
    clipStyle?: string;
    url?: string;
    asset?: string;
}

function isEvtWithFiles(event: React.DragEvent<HTMLLabelElement>) {
    if (!event.dataTransfer) {
        return !!event.target && !!(event as ChangeEvent<any>).target.files;
    }
    return Array.prototype.some.call(
        event.dataTransfer.types,
        (type) => type === 'Files' || type === 'application/x-moz-file'
    );
}

const ImageUploader = (
    {
        createAsset,
        removeAsset,
        discountId,
        isLoyaltyCard,
        clipStyle,
        asset,
    }: ImageUploaderProps
) => {
    const { t } = useTranslation();
    const [error, setError] = useState<Error>({
        error: false,
        message: '',
    });

    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    useEffect(() => {
        setIsUploading(false);
    }, [asset]);

    const onImageFileLoad = useCallback((event: ProgressEvent<FileReader>, image: HTMLImageElement, file: File) => {
        if (event.target && event.target.result) {
            const imageSrc = event.target.result.toString();
            image.src = imageSrc;
            image.onload = () => {
                const outputImage = onNewImageBuild(image);
                outputImage.toBlob(
                    (blob) => {
                        const size = blob?.size || 0;
                        if (size / 1024 / 1024 > 2) {
                            setError({
                                error: true,
                                message: t('marketing.campaign.too_big_file'),
                            });
                        } else if (blob) {
                            createAsset({
                                file: blob,
                                discountId,
                            });
                        }
                    },
                    file.type,
                    1
                );
            };
        }
    }, [createAsset, discountId, t]);

    const uploadFile = useCallback((file: File) => {
        setIsUploading(true);
        const newFile = file;
        const reader = new FileReader();
        const image = new Image();
        reader.onload = (event) => {
            onImageFileLoad(event, image, newFile);
        };
        reader.onerror = (event) => {
            setIsUploading(false);
            console.error(event.target?.error);
        };
        reader.readAsDataURL(newFile);
    }, [onImageFileLoad]);

    const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            uploadFile(event.target.files[0]);
        }
    };

    const onNewImageBuild = (image: HTMLImageElement) => {
        const aspectRatio = 4 / 3;
        const imageWidth = image.naturalWidth;
        const imageHeight = image.naturalHeight;
        const imageAspectRatio = imageWidth / imageHeight;
        let outputWidth = imageWidth;
        let outputHeight = imageHeight;
        if (imageAspectRatio > aspectRatio) {
            outputWidth = imageHeight * aspectRatio;
        } else if (imageAspectRatio < aspectRatio) {
            outputHeight = imageWidth / aspectRatio;
        }
        const outputX = (outputWidth - imageWidth) * 0.5;
        const outputY = (outputHeight - imageHeight) * 0.5;
        const outputImage = document.createElement('canvas');
        outputImage.width = outputWidth;
        outputImage.height = outputHeight;
        const ctx = outputImage.getContext('2d');
        ctx?.drawImage(image, outputX, outputY);
        return outputImage;
    };


    const overrideEventDefaults = (ev: React.DragEvent<HTMLLabelElement>) => {
        // Prevent default behavior (Prevent file from being opened)
        ev.preventDefault();
        ev.persist();
        ev.stopPropagation();
    };

    const dropHandler = useCallback((ev: React.DragEvent<HTMLLabelElement>) => {
        /**
         * For some reason, dropping files on Chrome doesn't always work, but does in Firefox.
         */
        setIsDragging(false);
        overrideEventDefaults(ev);
        if (isEvtWithFiles(ev)) {
            if (!ev.isPropagationStopped()) return;
            try {
                uploadFile(ev.dataTransfer.files[0]);
            } catch (e) {
                console.error(e?.message);
            }
        }
    }, [uploadFile]);

    const dragEnterHandler = useCallback((ev: React.DragEvent<HTMLLabelElement>) => {
        overrideEventDefaults(ev);
    }, []);

    const dragOverHandler = useCallback((ev: React.DragEvent<HTMLLabelElement>) => {
        setIsDragging(true);
        overrideEventDefaults(ev);
    }, []);

    const dragEndHandler = useCallback((ev: React.DragEvent<HTMLLabelElement>) => {
        setIsDragging(false);
        overrideEventDefaults(ev);
    }, []);

    return (
        <>
            <UploaderLabel>
                {t('marketing.campaign.image')}
                {error.error && <ErrorSpan>{error.message}</ErrorSpan>}
            </UploaderLabel>
            {!asset && !isUploading ? (
                <Uploader
                    onDrag={overrideEventDefaults}
                    onDragStart={overrideEventDefaults}
                    onDragEnd={overrideEventDefaults}
                    onDragEnter={dragEnterHandler}
                    onDrop={dropHandler}
                    onDragOver={dragOverHandler}
                    onDragLeave={dragEndHandler}
                >
                    <UploaderText isDragging={isDragging}>{t('marketing.campaign.add_image')}</UploaderText>
                    <UploaderInput
                        type="file"
                        name="image"
                        onChange={handleImageUpload}
                        accept="image/png, image/jpeg"
                    />
                </Uploader>
            ) : !asset && isUploading ? (
                <UploaderText isDragging={isDragging}>
                    <CenterLoading />
                </UploaderText>
            ) : isLoyaltyCard ? (
                <>
                    <ImageContainer>
                        <ImagePreview src={asset} />
                        <GridContainer>
                            {[...Array(10).keys()].map((index) => {
                                if (clipStyle === 'circles') return <Circle key={index} />;
                                if (clipStyle === 'stars') return <Star key={index} />;
                                if (clipStyle === 'diamonds') return <Diamond key={index} />;
                                if (clipStyle === 'squares') return <Square key={index} />;
                                return null;
                            })}
                        </GridContainer>
                    </ImageContainer>
                    <ClearImageWrapper>
                        <Button
                            className="alt tiny"
                            onClick={() => {
                                removeAsset(discountId);
                            }}
                        >
                            {t('marketing.campaign.clear_image')}
                        </Button>
                    </ClearImageWrapper>
                </>
            ) : (
                <>
                    <ImagePreview src={asset} />
                    <ClearImageWrapper>
                        <Button
                            className="alt tiny"
                            onClick={() => {
                                removeAsset(discountId);
                            }}
                        >
                            {t('marketing.campaign.clear_image')}
                        </Button>
                    </ClearImageWrapper>
                </>
            )}
        </>
    );
};

const CenterLoading = styled(Loading)`
    margin-left: auto;
    margin-right: auto;
`;


const UploaderLabel = styled.label`
    padding: ${distances.tiny} 0;
    font-weight: 700;
    display: block;
    opacity: 0.5;
`;
const ClearImageWrapper = styled.div`
    margin-top: 8px;
    text-align: right;
`;

const ErrorSpan = styled.span`
    color: ${colors.invalid};
    font-weight: 700;
    padding-left: ${distances.tiny};
`;

const Uploader = styled.label`
    cursor: pointer;
    z-index: 50;
`;

type UploaderTextProps = {
    isDragging: boolean;
};
const UploaderText = styled.div<UploaderTextProps>`
    color: ${colors.textSecondary};
    width: 100%;
    margin-top: 0;
    padding: 125px 0;
    text-align: center;
    border: ${border.normal} solid ${(props) => (props.isDragging ? colors.primaryHover : colors.borderDark)};
    border-radius: 2px;
    border-style: dashed;
    font-size: 14px;
    line-height: 22px;
    margin: 0;
    background: ${(props) => (props.isDragging ? colors.interactiveBackgroundHover : 'inherit')};
    &:hover {
        background: ${colors.interactiveBackgroundHover};
    }
    transform: ${(props) => (props.isDragging ? 'scale(0.98)' : 'scale(1)')};
    transition: all 0.7s cubic-bezier(.07,.95,0,1);
    pointer-events: none;
`;

const UploaderInput = styled.input`
    display: none;
`;

const ImageContainer = styled.div`
    position: relative;
`;

const ImagePreview = styled.img`
    width: 100%;
    object-fit: 'contain';
`;

const Circle = styled.div`
    height: 50px;
    width: 50px;
    background-color: ${colors.background};
    border-radius: 50%;
    display: inline-block;
`;

const GridContainer = styled.div`
    position: absolute;
    top: 30%;
    left: 5%;
    display: grid;
    grid-template-rows: repeat(2, 1fr);
    grid-template-columns: repeat(5, 1fr);
    column-gap: 5%;
    row-gap: 25%;
    width: 90%;
    height: 40%;
    justify-items: center;
`;

const Star = styled.div`
    height: 50px;
    width: 50px;
    clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
    background: ${colors.background};
`;

const Square = styled.div`
    height: 50px;
    width: 50px;
    background: ${colors.background};
`;

const Diamond = styled.div`
    height: 40px;
    width: 40px;
    transform: rotate(45deg);
    background: ${colors.background};
`;

export default ImageUploader;
